import { useEffect, useState } from 'react';
import { PROJECT_ID } from 'routes/BudgetModule/config/constants';
import { ColumnRowGroupChangedEvent } from 'ag-grid-community';
import { InscriptionWithMetadata } from 'routes/BudgetModule/config/types';
import { cloneDeep } from 'lodash';

/**
 * Compact inscriptions by grouping them on projects associations.
 * With project.id containing a list of associated projects.ids
 */
export const foldInscriptionsByProject = (
    inscriptionsWithPhase: InscriptionWithMetadata[],
): InscriptionWithMetadata[] => {
    const originalInscriptions = cloneDeep(inscriptionsWithPhase);
    const compactedInscriptions: {
        [uniqueKey: string]: InscriptionWithMetadata;
    } = {};

    originalInscriptions.forEach((originalInscription) => {
        const { [PROJECT_ID]: projectId, ...attributesWithoutProjectId } =
            originalInscription.inscription;
        const uniqueKey = JSON.stringify(attributesWithoutProjectId);

        if (!compactedInscriptions[uniqueKey]) {
            compactedInscriptions[uniqueKey] = originalInscription;
        } else {
            compactedInscriptions[uniqueKey].inscription[PROJECT_ID].push(
                ...projectId,
            );
        }
    });
    return Object.values(compactedInscriptions);
};

/**
 * Unfold inscriptions with multiple projects into individual inscriptions for each project.
 */
export const unfoldInscriptionsByProject = (
    inscriptionsWithMetadata: InscriptionWithMetadata[],
): InscriptionWithMetadata[] => {
    const unfoldedInscriptions: InscriptionWithMetadata[] = [];
    inscriptionsWithMetadata.forEach((inscriptionWithMetadata) => {
        const projects = inscriptionWithMetadata.inscription[PROJECT_ID];
        if (projects && projects.length > 1) {
            projects.forEach((projectId) => {
                const newInscription = JSON.parse(
                    JSON.stringify(inscriptionWithMetadata),
                );
                newInscription.inscription[PROJECT_ID] = [projectId];
                unfoldedInscriptions.push(newInscription);
            });
        } else {
            unfoldedInscriptions.push(inscriptionWithMetadata);
        }
    });
    return unfoldedInscriptions;
};

/**
 * Check if the project column is grouped in the grid
 */
const checkIsProjectColumnGrouped = (
    event: ColumnRowGroupChangedEvent,
): boolean =>
    Boolean(event.columns?.some((col) => col.getColDef().field === PROJECT_ID));

/**
 * Inscriptions can be prepared in two "modes" :
 * 1) compacted : with one inscription containing a list of projects
 * 2) unfolded : with one inscription containing ONE project, and being duplicated if associated with more than one project
 * Why ? Because business needs to group by project and AgGrid does not natively support the unfolding of lists groupby values.
 * This custom hook switches between theses modes if the grid is grouped by project or not.
 */
export const useManageInscriptionsDisplayModes = (
    inscriptions: InscriptionWithMetadata[],
) => {
    const [inscriptionsToDisplay, setInscriptionsToDisplay] =
        useState<InscriptionWithMetadata[]>(inscriptions);
    const [isProjectColumnGrouped, setIsProjectColumnGrouped] = useState(false);

    const handleGroupBySideEffectOnInscriptions = (
        event?: ColumnRowGroupChangedEvent,
    ) => {
        if (!event) {
            setIsProjectColumnGrouped(false);
            return;
        }
        setIsProjectColumnGrouped(checkIsProjectColumnGrouped(event));
    };

    useEffect(() => {
        const updateInscriptionsDisplayMode = () => {
            if (isProjectColumnGrouped) {
                setInscriptionsToDisplay(
                    unfoldInscriptionsByProject(inscriptions),
                );
            } else {
                setInscriptionsToDisplay(
                    foldInscriptionsByProject(inscriptions),
                );
            }
        };

        updateInscriptionsDisplayMode();
    }, [JSON.stringify(inscriptions), isProjectColumnGrouped]);

    return {
        inscriptionsToDisplay,
        handleGroupBySideEffectOnInscriptions,
    };
};
